/**
 * The round things and the plumes: silos, the tower, the palju's water, every
 * tuoppi in the building, and the smoke off the chimney and the water.
 *
 * Presentation. The palju's own bounds are hashed, but they are the hot tub's
 * spec rather than this cylinder.
 */

import { cylinder, type CylinderSpec, type SmokeSpec } from '../world/Level';
import { BAARI_PARTS, TUOPPIPOYTA_PARTS } from './levelBaari';
import { PALJUN_TUOPIT, POYTIEN_TUOPIT, tuoppi } from './levelJuhlasali';
import {
  AMPARI,
  JUPEN_VESIPULLO,
  KIMMON_TUOPPI,
  MATTRESS_TOP,
  POKAALIT,
  vesipullo,
} from './levelMakuuhuone';
import { MUURIKKA } from './levelMuurikka';
import { KAMIINA, PALJU, POYTARYHMA } from './levelPalju';
import {
  KUUSIKKO_ANSALANKA,
  KUUSIKKO_KAARRE,
  KUUSIKKO_PALJU,
  KUUSIKKO_PARKIN_PIELI,
  PAALIT,
} from './levelPiha';
import { TORNI } from './levelPorukka';
import { SAVUKONE, TELTAN_JALUSTAT } from './levelTeltta';

/* ============================================================ cylinders == */

export const cylinders: CylinderSpec[] = [
  // Oksennusampari, and what is in it. The contents sit on the bucket's top
  // cap rather than down inside it: a CylinderGeometry is closed at both ends,
  // so anything modelled at the honest level would simply never be seen. Full
  // to the brim is also, on this particular morning, the truthful reading.
  cylinder('oksennusampari', 'bucket', AMPARI.x, AMPARI.z, AMPARI.base, AMPARI.height, 0, {
    radialSegments: 12,
    topRadius: AMPARI.rim,
  }),
  cylinder(
    'oksennusampari-sisalto',
    'stain',
    AMPARI.x,
    AMPARI.z,
    AMPARI.rim - 0.015,
    0.02,
    AMPARI.height,
    { radialSegments: 12 },
  ),

  // Kimmo's four cups, on the yöpöytä at the head of his bed.
  ...POKAALIT.cylinders,

  // And what each of them had beside the pillow. See `YOPOYDAN_INSET`: the
  // arrangement is the same on both tables, so the contents are the joke.
  ...tuoppi('kimmon-yotuoppi', KIMMON_TUOPPI.x, KIMMON_TUOPPI.z, MATTRESS_TOP, KIMMON_TUOPPI.fill),
  ...vesipullo('jupen-vesipullo', JUPEN_VESIPULLO.x, JUPEN_VESIPULLO.z, MATTRESS_TOP),

  // The lipputanko. The box inside it is what you actually run into.
  cylinder('lipputanko-varsi', 'porcelain', 141.8, -28.3, 0.09, 9.0, 0.0, { radialSegments: 8 }),
  // The palju itself: water surface plus the outer shell.
  cylinder('palju-vesi', 'water', PALJU.x, PALJU.z, PALJU.radius, 0.06, 0.78),
  // The pipe you can find the palju by. The firebox under it is a solid rather
  // than a cylinder - it is a box stove standing in the open, and it is the one
  // thing at the palju you can actually run into.
  cylinder(
    'kamiina-piippu',
    'stove',
    KAMIINA.x,
    KAMIINA.z,
    KAMIINA.pipeRadius,
    KAMIINA.pipeTop - KAMIINA.height,
    KAMIINA.height,
    { radialSegments: 10 },
  ),
  // What was still on the long table when everybody went in.
  ...POYTARYHMA.cylinders,
  // Tornimajoitus - the old feed silo, now a bedroom, at the navetta's east
  // gable. See `TORNI`.
  cylinder('tornimajoitus', 'tower', TORNI.x, TORNI.z, TORNI.radius, TORNI.height, 0.0, {
    radialSegments: 20,
  }),
  cylinder('tornimajoitus-katto', 'lanRoof', TORNI.x, TORNI.z, TORNI.roofRadius, TORNI.roofHeight, TORNI.height, {
    radialSegments: 20,
    topRadius: 0.05,
  }),
  // The venue's own paljut, north of the covered terrace.
  cylinder('paljut-1', 'tub', 137.0, -47.5, 1.7, 1.0, 0.0, { radialSegments: 16 }),
  cylinder('paljut-2', 'tub', 141.0, -47.5, 1.7, 1.0, 0.0, { radialSegments: 16 }),
  // Naapurin siilo, and the box inside it is `siilo-runko`. Same drum as the
  // tornimajoitus, a good deal taller: from the aisle it is the one thing you
  // can see over the cars, and so the one thing that tells you how far down
  // the lot you have got.
  cylinder('siilo', 'tower', 59.5, -25.0, 2.12, 10.0, 0.0, { radialSegments: 16 }),
  cylinder('siilo-katto', 'lanRoof', 59.5, -25.0, 2.45, 1.6, 10.0, {
    radialSegments: 16,
    topRadius: 0.05,
  }),
  ...PAALIT.map((paali) => paali.drum),

  // The kuusikko along the right-hand edge. Cones, and every one of them has a
  // box inside it that is the thing you actually run into.
  ...KUUSIKKO_PARKIN_PIELI.crowns,
  ...KUUSIKKO_ANSALANKA.crowns,
  ...KUUSIKKO_PALJU.crowns,
  ...KUUSIKKO_KAARRE.crowns,

  // The juhlasali's baari: taps, kegs and what somebody poured out of them.
  ...BAARI_PARTS.cylinders,

  // And what the tables were left with once the bar had been poured out of.
  ...POYTIEN_TUOPIT,
  // The clean stacks on the table at the end of the bar, including the one
  // glass in the hall that is not scenery: the empty the porukka out on the
  // floor will not move without. See `TUOPPIPOYTA`.
  ...TUOPPIPOYTA_PARTS.cylinders,
  // The same glasses, out on the palju's rim, from before anybody got in.
  ...PALJUN_TUOPIT,

  /* ---- XLMOTO-teltan valorigin jalustat ---- */
  ...TELTAN_JALUSTAT,
];

/* ================================================================== savu == */

/**
 * What comes out of the pipe, and the wisp off the water under it.
 *
 * Purely rendering, like the cylinders it rises from: `World` loops a handful
 * of sprites through one climb, and nothing in the simulation knows the yard
 * has weather in it. Which also means these numbers are free to be changed on
 * looks alone - the fingerprint never sees them.
 *
 * It is a still, cold morning - the light in the scene is set for one - so the
 * column goes up before it goes anywhere. The lean it does have is small, and
 * it is the same lean for both plumes, because two columns of smoke five
 * metres apart do not disagree about the wind.
 */
const YARD_DRIFT: [number, number] = [1.1, 0.5];

export const smoke: SmokeSpec[] = [
  {
    name: 'kamiinan-savu',
    origin: { x: KAMIINA.x, y: KAMIINA.pipeTop, z: KAMIINA.z },
    rise: 5.2,
    drift: YARD_DRIFT,
    // Out of a 17 cm pipe, already a little wider than the pipe by the time it
    // is clear of it, and the width of the tub by the top of the climb. The
    // taper is what tells you which end of it is the fire.
    startRadius: 0.22,
    endRadius: 1.35,
    life: 3.6,
    // Enough of them that the column is continuous from the pipe up. Fewer and
    // it is a row of clouds; the run goes past this at 30 km/h and a gap in it
    // is the one thing the eye does catch.
    count: 18,
    density: 0.5,
  },
  {
    // Steam off the water, which is the point of the fire and therefore the
    // one thing that would be missed if it were not there. Thin, wide and slow
    // - it has to read as heat coming off a surface, not as a second chimney,
    // and Hannu has to stay visible through it.
    name: 'paljun-hoyry',
    origin: { x: PALJU.x, y: 0.9, z: PALJU.z },
    rise: 1.6,
    drift: [YARD_DRIFT[0] * 0.5, YARD_DRIFT[1] * 0.5],
    startRadius: 0.55,
    endRadius: 1.15,
    life: 5.0,
    count: 7,
    density: 0.18,
  },
  {
    // Breakfast, coming off a metre of hot plate. Thinner and shorter-lived
    // than either of the above, because it is water off food rather than a
    // fire: what it has to say from down the yard is that the pan is *on*,
    // and it has to say it without hiding the man standing behind it.
    name: 'muurikan-hoyry',
    origin: { x: MUURIKKA.x, y: MUURIKKA.height + 0.08, z: MUURIKKA.z },
    rise: 1.5,
    drift: [YARD_DRIFT[0] * 0.4, YARD_DRIFT[1] * 0.4],
    startRadius: 0.34,
    endRadius: 0.9,
    life: 4.0,
    count: 6,
    // Denser than either of the other two, and it still reads as the faintest
    // of the three - which is why this number is not the measure of how much
    // steam there is. A puff is warm grey, and what it has to be seen against
    // here is thirty metres of pale block wall lit by the same morning: at the
    // tub's 0.18 there is simply nothing on the screen. What keeps it a wisp
    // rather than a chimney is the climb and the width, and those are below.
    density: 0.6,
  },
  {
    // The savukone at the mouth of the XLMOTO tent, which is the one plume in
    // the yard that is not a fire - and therefore the one that is allowed to
    // be white. See `SAVUKONE` and `SmokeSpec.color`.
    //
    // It goes *out* rather than up, which is the whole difference between a
    // fog machine and a chimney: `rise` is barely over a man's waist and the
    // drift is four times it, so what the apron gets is a layer lying on the
    // grass rather than a column standing off it. That layer is what the
    // strobes and the lasers are visible in - see `DiscoSpec`.
    name: 'savukoneen-savu',
    origin: { x: SAVUKONE.minX - 0.1, y: 0.21, z: (SAVUKONE.minZ + SAVUKONE.maxZ) / 2 },
    rise: 1.25,
    drift: [-3.4, -1.4],
    startRadius: 0.26,
    endRadius: 1.9,
    // Long-lived and slow, because fog is: it is out of the nozzle in a second
    // and then it is weather for the next ten.
    life: 7.5,
    count: 14,
    density: 0.34,
    color: 0xf1f3f6,
  },
];
